/**
 * Asking before a change takes permissions away from the person making it.
 *
 * Five identity calls can do that — deleting a group or a role, removing a
 * group member, unassigning a role, replacing a role's permissions. The server
 * decides whether one does: it makes the change, measures, and answers 409
 * `self_lockout` with the permissions that would go, having changed nothing.
 * The interface does not try to predict it. Working out which of your roles
 * reach you through which group is the server's job, and a guess here would be
 * a second answer that could disagree with it.
 *
 * ```tsx
 * const lockout = useSelfLockout();
 * await lockout.guard((accept) => deleteGroup(id, accept));
 * …
 * {lockout.dialog}
 * ```
 *
 * `guard` tries the call. On `self_lockout` it opens the dialog, and on a yes
 * repeats the call with `acceptLockout` and re-reads `GET /auth/me`, so the
 * menu and the buttons stop offering what was just given up. On a no it
 * rethrows the 409, whose message says nothing was changed, for the caller to
 * show where it shows any other failure.
 *
 * The dialog can open on top of another one — a delete confirmation is still
 * open, pending, while this asks. That is deliberate: the first dialog keeps
 * its context and shows the outcome.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import DialogContentText from '@mui/material/DialogContentText';
import { ApiError } from '../api/client';
import type { Lockout } from '../api/identity';
import { ConfirmDialog } from '../components/ConfirmDialog';
import type { EntityActionsState } from '../hooks/useEntityActions';
import { useAuth } from './useAuth';

interface Question {
  permissions: string[];
  answer: (yes: boolean) => void;
}

export interface SelfLockoutState {
  /** Runs `call`, and asks and repeats it if the server says it locks you out. */
  guard: <T>(call: (lockout: Lockout) => Promise<T>) => Promise<T>;
  /** The question. Render it once, beside the screen's other dialogs. */
  dialog: ReactNode;
}

/** The permissions a 409 `self_lockout` lists, or `null` for any other failure. */
function lostPermissions(error: unknown): string[] | null {
  if (!(error instanceof ApiError) || error.code !== 'self_lockout') return null;
  return error.details?.permissions ?? [];
}

export function useSelfLockout(): SelfLockoutState {
  const { t } = useTranslation('people');
  const { refresh } = useAuth();
  const [question, setQuestion] = useState<Question | null>(null);

  const guard = useCallback(
    async <T,>(call: (lockout: Lockout) => Promise<T>): Promise<T> => {
      try {
        return await call({});
      } catch (error) {
        const permissions = lostPermissions(error);
        if (permissions === null) throw error;

        const yes = await new Promise<boolean>((answer) => setQuestion({ permissions, answer }));
        setQuestion(null);
        if (!yes) throw error;

        const result = await call({ acceptLockout: true });
        await refresh();
        return result;
      }
    },
    [refresh],
  );

  const dialog = question && (
    <ConfirmDialog
      open
      title={t('selfLockout.title')}
      tone="error"
      confirmLabel={t('selfLockout.confirm')}
      message={
        <>
          <DialogContentText>{t('selfLockout.intro')}</DialogContentText>
          <Box component="ul" sx={{ mt: 0.5, mb: 1.5, pl: 3 }}>
            {question.permissions.map((permission) => (
              <li key={permission}>
                <code>{permission}</code>
              </li>
            ))}
          </Box>
          <DialogContentText>{t('selfLockout.consequence')}</DialogContentText>
        </>
      }
      onConfirm={() => question.answer(true)}
      onClose={() => question.answer(false)}
    />
  );

  return { guard, dialog };
}

/**
 * An entity's action set with the question added to its dialogs, so every
 * screen that mounts the set's dialogs mounts this one too. For
 * `useGroupActions` and `useRoleActions`, whose delete can lock you out.
 */
export function withLockoutDialog<Row>(
  state: EntityActionsState<Row>,
  dialog: ReactNode,
): EntityActionsState<Row> {
  return {
    ...state,
    dialogs: (
      <>
        {state.dialogs}
        {dialog}
      </>
    ),
    forRow: (row: Row) => {
      const affordances = state.forRow(row);
      return {
        ...affordances,
        dialogs: (
          <>
            {affordances.dialogs}
            {dialog}
          </>
        ),
      };
    },
  };
}
